import { createFileRoute } from '@tanstack/react-router'
import { Rpc } from 'accounts'
import { RpcResponse } from 'ox'
import { useEffect } from 'react'
import { Frame } from 'regen-ui'
import { tempo } from 'viem/chains'
import { useConnection } from 'wagmi'
import * as z from 'zod/mini'

import * as Bridge from '#/lib/bridge.js'
import { getSupportedChainId } from '#/lib/chain.js'
import { useDeposit } from '../-hooks/useDeposit.js'
import * as FiatOnrampAccess from '../-lib/FiatOnrampAccess.js'
import * as PostMessage from '../-lib/PostMessage.js'
import * as RemoteRpc from '../-lib/RemoteRpc.js'

export const Route = createFileRoute('/remote/rpc/wallet_deposit')({
  component: Component,
  validateSearch: (search) => RemoteRpc.validateSearch(search, { method: 'wallet_deposit' }),
})

function Component() {
  const search = Route.useSearch()
  const rpc = RemoteRpc.use()
  const { address: connectedAddress, chainId: connectedChainId } = useConnection()
  const params = search._decoded?.params?.[0]
  const address = params?.address ?? connectedAddress
  const amount = params?.amount
  const displayName = params?.displayName
  const depositChainId = getSupportedChainId(params?.chainId ?? connectedChainId)
  const isTestnet = depositChainId !== tempo.id
  const unsupportedToken =
    params?.token && !Bridge.resolveToken(params.token) ? params.token : undefined
  const intent = (
    params as {
      intent?: 'applePay' | 'credits' | 'crypto' | 'faucet' | 'referralCode'
    }
  )?.intent
  const fiatBlocked =
    FiatOnrampAccess.isFundingIntent(intent) &&
    !FiatOnrampAccess.isAllowed(rpc.consumer.origin, PostMessage.origin())

  useEffect(() => {
    if (!unsupportedToken) return
    rpc.reject(
      new RpcResponse.InvalidParamsError({
        message: `Unsupported deposit token "${unsupportedToken}".`,
      }),
    )
  }, [rpc, unsupportedToken])

  useEffect(() => {
    if (!fiatBlocked) return
    rpc.reject(
      new RpcResponse.InvalidParamsError({
        message: 'Fiat funding is not available for this app.',
      }),
    )
  }, [fiatBlocked, rpc])

  const deposit = useDeposit({
    address,
    amount,
    chainId: depositChainId,
    initialScreen: getInitialScreen({ intent, isTestnet }),
    onSuccess: ({ receipts }) =>
      void rpc.resolve(
        receipts
          ? { receipts: receipts.map((r) => z.encode(Rpc.receipt as never, r as never)) }
          : undefined,
      ),
    successScreen: false,
    token: params?.token,
  })

  if (fiatBlocked || unsupportedToken) return null
  if (deposit.frame) return deposit.frame

  const subtitle = displayName ? (
    <>
      Deposit funds to <span className="text-foreground">{displayName}</span>.
    </>
  ) : (
    'Deposit funds into your Tempo account.'
  )

  return (
    <Frame subtitle={subtitle} title="Deposit">
      {deposit.actions}
    </Frame>
  )
}

function getInitialScreen(options: {
  intent?: 'applePay' | 'credits' | 'crypto' | 'faucet' | 'referralCode' | undefined
  isTestnet: boolean
}): useDeposit.Screen {
  const { intent, isTestnet } = options
  if (isTestnet) return 'default'
  if (intent === 'applePay') return 'deposit-apple-pay'
  if (intent === 'credits') return 'deposit-credits'
  if (intent === 'crypto') return 'deposit-crypto'
  if (intent === 'referralCode') return 'deposit-referral-code'
  return 'default'
}
